<script setup>
import { watch, ref } from 'vue';
import { useStateStore } from '@/stores/state.store';
import { storeToRefs } from 'pinia';
import ButtonDefault from './atoms/ButtonDefault.vue';

const state = useStateStore();
const { sideSheetOpen } = storeToRefs(state);
const sideSheet = ref()

const open = defineModel({
  type: Boolean,
  default: false,
});

const emit = defineEmits(['open', 'close']);

defineProps({
  title: {
    type: String,
  },
  headerAlignment: {
    type: String, // center, left
    default: "center",
  },
  position: {
    type: String, // right, left
    default: "right",
  },
  raw: {
    type: Boolean,
    default: false,
  },
  zIndex: {
    type: Number,
    default: 101,
  },
  className: {
    type: String,
    default: '',
  },
  displayStyle: {
    type: String,
    default: 'normal',
  },
  extraStyles: {
    type: Object,
  },
  iconOnly: {
    type: Boolean,
    default: false,
  },
  buttonType: {
    type: String,
    default: 'transparent',
  },
})

watch(open, (newVal) => {
  sideSheetOpen.value = newVal;

  if (newVal === false) {
    emit('close');
  } else {
    emit('open');
    if (sideSheet.value) {
      sideSheet.value.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
})
</script>

<template>
  <div
    class="side-sheet"
    ref="sideSheet"
    :style="{'z-index': zIndex, ...extraStyles}"
    :class="[`side-sheet--${position}`, `${className}`, `style--${displayStyle}`, {'open': open, 'side-sheet--raw': raw}]" >
    <div class="side-sheet__header" :class="{'side-sheet__header--no-title': !title }">
      <div class="container">
        <h1 v-if="title" class="side-sheet__header-title" :class="[`align--${headerAlignment}`]">
          {{ title }}
        </h1>

        <div class="side-sheet__header-controls" :class="{ 'padded': !iconOnly }">
          <ButtonDefault
            icon="Close"
            class="side-sheet__close"
            :type="buttonType"
            :icon-only="iconOnly"
            @click="open = false"
          />
        </div>
      </div>
    </div>

    <slot />
  </div>
</template>

<style lang="scss" scoped>
.side-sheet {
  $padding: 24px;
  --bg: var(--clr-white);
  overscroll-behavior: none;
  position: fixed;
  top: 0;
  width: 600px;
  height: 100dvh;
  background-color: var(--bg);
  overflow-y: auto;
  overflow-x: clip;
  z-index: 101;

  @media (max-width: 767px) {
    width: 100vw;
  }

  &--right {
    right: -101%;
    transition: right 0.3s ease;
    &.open {
      right: 0;
    }
  }

  &--left {
    left: -100%;
    transition: left 0.3s ease;
    &.open {
      left: 0;
    }
  }

  &--raw {
    padding: 0;
  }

  &--raw &__header {
    min-height: 0;
    z-index: 1;
    width: unset;
    top: 0;

    &--with-title {
      margin: 0;
      top: 0;
    }
  }

  &__header {
    position: sticky;
    top: 0;
    left: 0;
    padding: 0 $padding;
    height: 56px;
    background-color: var(--bg);
    z-index: 1000;

    &--no-title {
      margin-bottom: -56px;
      background: transparent;
      padding: $padding 0
    }

    .container {
      position: relative;
      display: flex;
      align-items: center;
      height: 56px;
    }

    .side-sheet__header-title {
      width: 100%;
      font-weight: 500;
    }

    .side-sheet__header-controls {
      height: 56px;
      position: absolute;
      top: 0;
      right: 0;
      display: flex;
      align-items: center;
      background-color: transparent;
      color: inherit;

      &.padded {
        right: $padding;
      }
    }
  }
}
</style>
